import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Check, MessageCircle, ChevronLeft, ShieldCheck, Leaf, Store, Award } from "lucide-react";
import { useMarketProduct, useSite } from "@/lib/api";
import Seo from "@/components/common/Seo";
import SectionHeading from "@/components/common/SectionHeading";
import MarketCard from "@/components/products/MarketCard";
import QuantityPicker from "@/components/order/QuantityPicker";
import { formatPrice, lineEstimate, ADET_OPTIONS } from "@/lib/site";
import { cn } from "@/lib/utils";
import { useOrderList } from "@/context/OrderListContext";

const TRUST = [
  { icon: ShieldCheck, text: "1985'ten beri Ertan Kasap güvencesi" },
  { icon: Leaf, text: "Özenle seçilmiş, kaliteli ürün" },
  { icon: Store, text: "Şubeden taze tedarik" },
];

export default function MarketDetail() {
  const { slug } = useParams();
  const { data, isLoading } = useMarketProduct(slug);
  const { data: site } = useSite();
  const { addItem, isInList } = useOrderList();
  const [qty, setQty] = useState("1 adet");

  if (isLoading || !data?.product) {
    return (
      <div className="mx-auto max-w-7xl px-4 py-32 text-center font-mono text-sm text-charcoal/50" data-testid="market-detail-loading">
        Ürün yükleniyor...
      </div>
    );
  }

  const p = data.product;
  const inList = isInList(p.slug);
  const estimate = Math.round(lineEstimate({ price: p.price, unit: "adet", quantity: qty }) || p.price);

  const handleAdd = () => {
    addItem({ ...p, kind: "market", unit: "adet" }, qty);
    toast.success(`${p.name} (${qty}) sipariş listene eklendi`);
  };

  const askUrl = site?.whatsapp
    ? `https://wa.me/${site.whatsapp}?text=${encodeURIComponent(`Merhaba ${site.brand || "Ertan Kasap"}, "${p.name}" ürünü hakkında bilgi almak istiyorum.`)}`
    : "#";

  return (
    <div data-testid="market-detail-page" className="bg-cream pb-24">
      <Seo
        title={p.name}
        description={p.short_description || p.description}
        image={p.image}
        type="product"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Product",
          name: p.name,
          image: [(process.env.REACT_APP_SITE_URL || window.location.origin) + p.image],
          description: p.description || p.short_description,
          category: p.group_label,
          brand: { "@type": "Brand", name: "Ertan Kasap" },
          ...(p.price != null && {
            offers: {
              "@type": "Offer",
              price: p.price,
              priceCurrency: "TRY",
              availability: "https://schema.org/InStock",
              url: window.location.href,
            },
          }),
        }}
      />

      {/* HERO */}
      <section className="border-b border-dashed border-bordo/30">
        <div className="mx-auto max-w-7xl px-4 pt-6 sm:px-6 lg:px-8">
          <Link to="/market" className="inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-wider text-charcoal/50 transition-colors hover:text-bordo">
            <ChevronLeft className="h-4 w-4" /> Market & Şarküteri
          </Link>
        </div>
        <div className="mx-auto grid max-w-7xl gap-10 px-4 py-8 sm:px-6 lg:grid-cols-2 lg:px-8 lg:py-12">
          <div className="relative overflow-hidden border-4 border-charcoal bg-[#120D0A]">
            <img src={p.image} alt={p.name} className="aspect-[4/5] w-full object-cover" />
            <span className="absolute left-0 top-5 bg-bordo px-4 py-1.5 font-mono text-xs uppercase tracking-[0.15em] text-cream">
              {p.subcategory}
            </span>
          </div>

          <div className="flex flex-col justify-center">
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-bordo">{p.group_label}</span>
            {p.badge && (
              <span data-testid="market-detail-badge" className="mt-3 inline-flex w-fit items-center gap-1.5 rounded-full border border-copper/30 px-2.5 py-[3px] font-mono text-[10px] uppercase tracking-[0.2em] text-copper/90">
                <Award className="h-3 w-3 text-copper/70" strokeWidth={1.5} /> {p.badge}
              </span>
            )}
            <h1 className="mt-2 font-serif text-4xl leading-tight tracking-tight text-charcoal sm:text-5xl">{p.name}</h1>
            <p className="mt-4 text-lg leading-relaxed text-[#6b645c]">{p.description || p.short_description}</p>

            <div className="mt-7 border border-dashed border-bordo/30 bg-paper p-5">
              <span className="font-mono text-xs uppercase tracking-wider text-charcoal/50">Adet seç ve listene ekle</span>
              {p.price != null && (
                <div className="mt-3 flex items-baseline justify-between border-b border-dashed border-bordo/20 pb-3">
                  <span className="font-mono text-xs text-charcoal/55">
                    {formatPrice(p.price)} <span className="text-charcoal/40">/ {p.unit || "adet"}</span>
                  </span>
                  <span className="text-right">
                    <span className="block font-mono text-[10px] uppercase tracking-wider text-charcoal/45">Seçili ({qty})</span>
                    <span data-testid="market-detail-price" className="font-serif text-3xl leading-none text-bordo">
                      ≈ {formatPrice(estimate)}
                    </span>
                  </span>
                </div>
              )}
              <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-center">
                <QuantityPicker testid="market-detail-quantity" value={qty} onChange={setQty} options={ADET_OPTIONS} />
                <button
                  data-testid="market-detail-add"
                  onClick={handleAdd}
                  className={cn(
                    "inline-flex flex-1 items-center justify-center gap-2 rounded-sm px-5 py-3 text-sm font-bold uppercase tracking-wide transition-colors",
                    inList ? "bg-charcoal text-cream" : "bg-bordo text-cream hover:bg-bordo-dark"
                  )}
                >
                  {inList ? <Check className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                  {inList ? "Listene Eklendi" : "Listeme Ekle"}
                </button>
              </div>
              <a
                href={askUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="market-detail-whatsapp"
                className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-sm border border-[#25D366] px-5 py-3 text-sm font-bold uppercase tracking-wide text-[#1a9e4b] transition-colors hover:bg-[#25D366] hover:text-charcoal"
              >
                <MessageCircle className="h-4 w-4" /> WhatsApp&apos;tan Sor
              </a>
            </div>

            <div className="mt-6 grid gap-2.5 sm:grid-cols-3">
              {TRUST.map(({ icon: Icon, text }) => (
                <div key={text} className="flex items-center gap-2 border border-dashed border-copper/30 bg-paper px-3 py-2.5">
                  <Icon className="h-4 w-4 flex-shrink-0 text-bordo" />
                  <span className="text-[11px] leading-tight text-charcoal/70">{text}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* BUNU ALANLAR BUNU DA EKLEDİ */}
        {data.also_added?.length > 0 && (
          <section className="border-b border-dashed border-bordo/30 py-14" data-testid="market-also-added">
            <SectionHeading as="h2" eyebrow="Birlikte Tercih Edilenler" title="Bunu Alanlar Bunu da Ekledi" />
            <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
              {data.also_added.map((rp) => <MarketCard key={rp.slug} product={rp} />)}
            </div>
          </section>
        )}

        {/* AYNI REYONDAN */}
        {data.same_group?.length > 0 && (
          <section className="border-b border-dashed border-bordo/30 py-14" data-testid="market-same-group">
            <SectionHeading as="h2" eyebrow={p.group_label} title="Aynı Reyondan" />
            <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
              {data.same_group.map((rp) => <MarketCard key={rp.slug} product={rp} />)}
            </div>
          </section>
        )}

        {/* KASABA SOR CTA */}
        <section className="py-14">
          <div className="flex flex-col items-center bg-bordo px-6 py-12 text-center sm:px-12">
            <MessageCircle className="h-10 w-10 text-cream/70" strokeWidth={1.4} />
            <h2 className="mt-5 font-serif text-3xl leading-tight text-cream sm:text-4xl">
              {p.name} hakkında bir sorunuz mu var?
            </h2>
            <p className="mt-4 max-w-xl text-base leading-relaxed text-cream/80">
              Stok, gramaj ya da tedarik hakkında merak ettiğinizi WhatsApp&apos;tan yazın; ekibimiz hemen yardımcı olsun.
            </p>
            <a
              href={askUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="market-detail-cta"
              className="mt-8 inline-flex items-center gap-2.5 rounded-sm bg-cream px-8 py-4 text-base font-bold uppercase tracking-wide text-bordo transition-transform hover:scale-[1.02]"
            >
              <MessageCircle className="h-5 w-5" /> WhatsApp&apos;tan Yaz
            </a>
          </div>
        </section>
      </div>
    </div>
  );
}
